import { MutableRefObject, RefObject, useLayoutEffect, useRef } from 'react';

import { RLottieHandler } from '@gem/modules/rlottie';

import { animationsCache } from 'APP/cache/animations';

const getRLottieHandler = (
  url: string | undefined,
  animationData: { id?: string; data: any } | undefined,
  container: HTMLElement,
  options: {
    loop: boolean;
    autoplay: boolean;
    onLoopComplete?: (handler: RLottieHandler) => void;
    onEnterFrame?: (handler: RLottieHandler) => void;
  }
): RLottieHandler | null => {
  if (!url && !animationData?.id) return null;

  const newHandler = new RLottieHandler({
    url,
    loop: options.loop,
    autoplay: options.autoplay,
    onLoopComplete: options.onLoopComplete,
    onEnterFrame: options.onEnterFrame,
    container,
    animationData: { id: animationData?.id, data: animationData?.data },
    animationsCache,
  });

  return newHandler;
};

type IUseAnimateStickerProps = {
  url?: string;
  autoplay?: boolean;
  animationData?: {
    id: string;
    data: Record<string, any>;
  };
  loop?: boolean;
  onEnterFrame?(handler: RLottieHandler): void;
  onLoopComplete?(handler: RLottieHandler): void;
};

interface IUseAnimateStickerReturn<TRef> {
  animationRef: MutableRefObject<RLottieHandler | undefined>;
  stickerContainerRef: RefObject<TRef>;
}

export const useAnimateSticker = <TRef = Element>(
  props: IUseAnimateStickerProps
): IUseAnimateStickerReturn<TRef> => {
  const { url, animationData, autoplay, loop = true, onEnterFrame, onLoopComplete } = props;

  const stickerContainerRef = useRef<TRef>(null);
  const animationRef = useRef<RLottieHandler>();

  useLayoutEffect(() => {
    const container = stickerContainerRef.current as HTMLElement;
    if (!container) {
      return;
    }

    const handler = getRLottieHandler(url, animationData, container, {
      loop: loop || true,
      autoplay: autoplay || false,
      onLoopComplete,
      onEnterFrame,
    });

    if (!handler) {
      return;
    }

    animationRef.current = handler;

    return () => {
      animationRef.current?.destroy();
      animationRef.current = undefined;
    };
  }, [url, animationData]);

  return { animationRef, stickerContainerRef };
};
